// Persists the last successful hotel search in sessionStorage so the Results
// and Detail pages survive a refresh without re-hitting the supplier.
//
// The detail page reads this rather than re-searching, which mirrors what the
// backend does: a hotel `offer_id` is search-scoped, so re-running the search to
// open one hotel would produce fresh offers with a shorter life than the ones
// already held. Scoped to the current tab; cleared on sign-out by the auth
// context's qc.clear() at the query layer.

import type { HotelSearchResponse, HotelSource, Occupancy } from "./hotelTypes";

export interface CachedHotelSearch {
  // What the user typed — used to rehydrate the search bar on Results.
  form: {
    city_name: string;
    country_code: string;
    country_name?: string;
    check_in: string;
    check_out: string;
    occupancies: Occupancy[];
    nationality?: string;
    currency?: string;
    /** Which provider served this search. Re-sent verbatim on a re-search so a
     *  retry can't silently land on a different upstream. */
    source?: HotelSource;
  };
  response: HotelSearchResponse;
  fetched_at: number;
}

const KEY = "neptune.lastHotelSearch";

export function saveHotelSearch(c: CachedHotelSearch): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(c));
  } catch {
    // Quota exceeded or storage disabled — non-fatal, the user re-searches.
  }
}

export function loadHotelSearch(): CachedHotelSearch | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedHotelSearch;
  } catch {
    return null;
  }
}

export function clearHotelSearch(): void {
  sessionStorage.removeItem(KEY);
}
